import {createContext, useContext, useMemo, useCallback, useState} from "react";
import useSWR from "swr";
import {useHasCompany} from "../../utils/hooks/company";
import { useFirebase, useOnFirebaseMessage } from "./firebaseContext";
import { useMapNotifications } from "./useMapNotifcations";
import { useReadNotifications } from "./useReadNotifications";
import { useTranslation } from "react-i18next";

const NotificationsContext = createContext()

// open-shift-available is reset to unread with a new createdAt instead of being re-created,
// so the list must always be ordered by createdAt rather than by insertion
const sortByCreatedAt = (items) => [...items].sort(
    (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
);

export const NotificationsProvider = ({ children }) => {
    const hasCompany = useHasCompany();
    const { t } = useTranslation();
    const { readOne, readAll } = useReadNotifications();

    const [page, setPage] = useState(1);
    const {
        data: { result: data, totalItems },
        isLoading,
        mutate,
    } = useSWR(
        hasCompany ? {
            url: "/notifications",
            params: { page, perPage: 10 },
        } : null,
        { fallbackData: { result: [], totalItems: 0 } },
    );

    // the unread total comes from the same list endpoint filtered by read: false, only totalItems is used
    const {
        data: { totalItems: unreadNotifications },
        mutate: mutateUnreadList,
    } = useSWR(
        hasCompany ? {
            url: "/notifications",
            params: { page: 1, perPage: 1, read: false },
        } : null,
        { fallbackData: { result: [], totalItems: 0 } },
    );

    // accepts a number or an updater (prev count => next count); no argument revalidates
    const mutateUnread = useCallback((...args) => {
        if (args.length === 0) {
            return mutateUnreadList();
        }
        const [update, options] = args;
        return mutateUnreadList((prev) => {
            const prevCount = prev?.totalItems || 0;
            const totalItems = typeof update === "function" ? update(prevCount) : update;
            return { ...(prev || { result: [] }), totalItems };
        }, options);
    }, [mutateUnreadList]);

    const updateList = useCallback((update) => {
        mutate((prev) => update(prev || { result: [], totalItems: 0 }), { revalidate: false });
    }, [mutate]);

    const refresh = useCallback(() => {
        mutate();
        mutateUnread();
    }, [mutate, mutateUnread]);

    const markRead = useCallback(async (id) => {
        updateList((prev) => ({
            ...prev,
            result: prev.result.map((item) => (item.id === id ? { ...item, read: true } : item)),
        }));
        mutateUnread((prev) => Math.max((prev || 0) - 1, 0), { revalidate: false });
        try {
            await readOne(id);
        } finally {
            // a refetch that started before the PUT finished (bell open, route change, focus) brings back
            // read: false and overwrites the optimistic update, so reload both once the server has it
            refresh();
        }
    }, [updateList, mutateUnread, readOne, refresh]);

    const markAllRead = useCallback(async () => {
        updateList((prev) => ({
            ...prev,
            result: prev.result.map((item) => ({ ...item, read: true })),
        }));
        mutateUnread(0, { revalidate: false });
        try {
            await readAll();
        } finally {
            refresh();
        }
    }, [updateList, mutateUnread, readAll, refresh]);

    const hasMore = useMemo(() => {
        return data.length < totalItems;
    }, [data.length, totalItems]);

	const fetchMore = useCallback(() => {
		setPage((prev) => prev + 1);
	}, [setPage]);

    const onMessage = useCallback((notification, isBacground = false) => {
        let notData = {};
        try {
            notData = JSON.parse(notification.data?.notification);
        } catch (err) {
        }
        if(notData && Object.keys(notData).length > 0) {
            updateList(({ result: prev, totalItems: prevTotalItems }) => {
                const exists = prev.some((item) => item.id === notData.id);
                return {
                    result: [
                        { ...notData, read: false },
                        ...prev.filter((item) => item.id !== notData.id),
                    ],
                    totalItems: exists ? prevTotalItems : prevTotalItems + 1,
                };
            });
            // the same notification can come back as unread, so ask the server instead of incrementing
            mutateUnread();
        }
    }, [updateList, mutateUnread]);

    const sorted = useMemo(() => sortByCreatedAt(data), [data]);
    const notifications = useMapNotifications(sorted, t);

    useOnFirebaseMessage(onMessage);
    const { firebaseToken } = useFirebase();
    const value = useMemo(() => ({
        loading: isLoading,
        hasMore,
        totalItems,
        notifications,
        markRead,
        markAllRead,
        refresh,
        refreshUnread: mutateUnread,
        fetchMore,
        firebaseToken,
        unreadNotifications: unreadNotifications || 0,
    }), [
        notifications,
        markRead,
        markAllRead,
        refresh,
        mutateUnread,
        isLoading,
        hasMore,
        totalItems,
        fetchMore,
        firebaseToken,
        unreadNotifications,
    ]);

    return (
        <NotificationsContext.Provider value={value}>
            {children}
        </NotificationsContext.Provider>
    );
}

export const useNotifications = () => {
    const context = useContext(NotificationsContext);
    if (context === undefined) {
        throw new Error("");
    }
    return context;
}
